import KeyboardDismissView from "@app/hoc/KeyboardDismissView";
import ScreenWrapper from "@app/hoc/ScreenWrapper";
import { PrivateStackParamList } from "@app/navigationApp/types";
import CommonHeader from "@app/sharedComponents/Header/Header";
import {  useAppDispatch, useAppSelector } from "@app/store/hooks";
import { Theme, ThemedText, ThemedView } from "@app/theme";
import { addCommasToNumber, getDesiredValue, getTotalLimit, getUpdateAvailableCAS, roundDownToNearestHundred, useDarkMode } from "@app/utils";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { ScrollView } from "react-native";
import { getApplicationId } from "../FetchPortfolio/utils";
import { endpoints } from "@app/api/apis";
import axios from "axios";
import { useEffect, useState } from "react";
import MutualFundList from "../UnlockLimitLanding/components/MutualFundsList/MutualFundsList";
import Button from '@sharedComponents/Button/Button';
import { useIsEnhanceLimit } from "@app/hooks/useIsEnhanceLimit";
import ROUTE from "@app/navigationApp/routes";
import handleApiErr from "@app/helper/handleApiErr";
import { showErrorSheet } from "@app/store/slices/errorSlice";
import { useTheme } from "@shopify/restyle";

type PortfolioProps = NativeStackScreenProps<PrivateStackParamList, 'PORTFOLIO'> & {

}

const Portfolio = ({
    navigation,
    route
}: PortfolioProps) => {
    const { userDetails } = useAppSelector((state) => state.auth);
    const { authCAS, availableCAS , minimumEligibleLimit } = useAppSelector(state => state.application);
    const [portValue, setPortValue] = useState(0);
    const [btnLoading, setBtnLoading] = useState(false);
    const { isFromEnhanceLimit } = useIsEnhanceLimit();
    const {colors} = useTheme<Theme>()
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (authCAS) {
            setPortValue(authCAS?.totalPortfolioAmount);
        }
    }, [authCAS]);

    useEffect(() => {
        if (availableCAS && authCAS?.isinNAVMap) {
            const portValue = getDesiredValue(
                Object.values(availableCAS),
                authCAS?.isinNAVMap
            );
            setPortValue(portValue);
        }
    }, [availableCAS]);

    const savePledgeLimit = async () => {
        let pledgeDataToSave = availableCAS && Object.values(availableCAS).filter((item) => item.pledgedUnits > 0);
        pledgeDataToSave = pledgeDataToSave && pledgeDataToSave.map((item) => ({
            ...item,
            is_pledged: item.pledgedUnits > 0 ? true : false
        }));
        setBtnLoading(true);
        const applicationId = await getApplicationId(userDetails?.linkedApplications[0]?.applicationId);
        const url = endpoints.pledgeSave;
        await axios.post(url, {
            applicationId: applicationId,
            portfolioItemList: pledgeDataToSave
        }).then((response) => {
            if(isFromEnhanceLimit) {
                navigation.navigate(ROUTE.LOAN_OFFER_ENHANCE_LIMIT);
            } else {
                navigation.navigate(ROUTE.PLEDGE_CONFIRMATION);
            }
            setBtnLoading(false);
        }).catch((error) => {
            if (error?.data?.statusCode && handleApiErr(error)) {
                dispatch(showErrorSheet({
                    isErrorSheetVisible: true,
                    errorMessage: error?.data?.message,
                    closeOnBackdropPress: true,
                    errorType: error?.data?.voltErrorCode || error?.data?.statusCode,
                    errorHeading: error?.data?.header,
                    showErrorHeading: true,
                    btnName: 'Ok, got it!',
                }))
            }
            setBtnLoading(false);
        });
    }

    const outOfValue = availableCAS && authCAS && addCommasToNumber(
        roundDownToNearestHundred(
            getTotalLimit(
                Object.values(availableCAS),
                authCAS.isinNAVMap,
                authCAS.isinLTVMap
            )
        )
    )

    const outOfText = `₹${outOfValue}`

    const totalText = authCAS && `₹${addCommasToNumber(authCAS?.availableCreditAmount || 0)}`;

    const ctaDisabled = parseInt(outOfValue?.replace(/,/g, '') ?? '0') < (minimumEligibleLimit?.amount ?? 10000)

    return (<>
        <ScreenWrapper screenName={route.name}>
                <ThemedView flex={1} backgroundColor="bg-surface-primary">
                    <CommonHeader
                        label='Set credit limit'
                        navigation={navigation}
                        pageName={route.name}
                    />
                    <ScrollView
                        style={{ flex: 1, backgroundColor: colors['bg-fill-primary'] }}
                        showsVerticalScrollIndicator={false}
                        keyboardShouldPersistTaps="handled"
                    >
                        <ThemedView style={{ flex: 1 }}>
                            <ThemedView style={{ paddingHorizontal: 16 }}>
                                <ThemedView style={{ height: 16 }} />
                                <ThemedText variant="h5s" color="text-primary">
                                    Select mutual funds for pledging
                                </ThemedText>
                                <ThemedText variant="b3r" color="text-secondary">
                                    ₹{addCommasToNumber(portValue || 0)} out of ₹{addCommasToNumber(authCAS?.totalPortfolioAmount || 0)} are selected for pledging.
                                </ThemedText>
                                <ThemedView style={{ height: 8 }} />
                                <MutualFundList editMode={true} />
                            </ThemedView>
                        </ThemedView>
                    </ScrollView>
                    <ThemedView
                        backgroundColor='bg-fill-primary'
                        borderTopWidth={1}
                        borderTopColor='border-primary'
                    >
                        <ThemedView style={{ paddingHorizontal: 16 }}>
                            <ThemedView style={{ height: 16 }} />
                            <ThemedText variant="b3r" color="text-secondary">
                                Selected credit limit
                            </ThemedText>
                            <ThemedView style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <ThemedText variant="b1s" color="text-primary">
                                    {outOfText}
                                </ThemedText>
                                <ThemedText variant="b4r" color="text-secondary">
                                    / {totalText}
                                </ThemedText>
                            </ThemedView>
                        </ThemedView>
                        <ThemedView m="lg">
                            <Button
                                eventName={route.name + '_CONFIRM_LIMIT_AND_PROCEED_BUTTON_CLICKED'}
                                label={'Confirm limit and proceed'}
                                handlePress={() => {
                                    !btnLoading && savePledgeLimit();
                                }}
                                type={'SOLID'}
                                size='MD'
                                bg='bg-primary'
                                textColor='text-primary'
                                disabled={ctaDisabled}
                                buttonLoader={btnLoading || btnLoading}
                            />
                        </ThemedView>
                    </ThemedView>
                </ThemedView>
        </ScreenWrapper>
    </>)
}

export default Portfolio;